/**
 * Tâche 1250 (Eric 2026-06-28) — Sentinelle Resend, pas à pas.
 *
 * L'incident « provider:resend » est revenu 17 fois : « The knoria.com domain
 * is not verified ». Le guide de vérification du domaine (tâche 1151) vivait
 * sur la page Forfait AI — loin de l'endroit où l'alerte est lue. Il est
 * maintenant ici, avec la dernière étape qui manquait : relancer le test
 * d'envoi et voir la réponse de Resend, sans quitter la page.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PaperPlaneTilt, SpinnerGap, CheckCircle, XCircle } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import EmailDomainGuide from "./EmailDomainGuide";

const API = process.env.REACT_APP_BACKEND_URL;

export const ResendSentinelCard = () => {
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState(null);

  const runTest = async () => {
    setTesting(true);
    setTest(null);
    try {
      // On passe par le MÊME chemin que les courriels réels
      // (`/api/admin/test-email`) : tester la clé BYOK ne dirait rien quand
      // l'organisation utilise la clé Knoria partagée.
      const { data } = await axios.post(`${API}/api/admin/test-email`, {}, {
        withCredentials: true,
        timeout: 60000,
      });
      setTest(data);
      if (data.status === "suppressed") toast.warning("Envoi bloqué par la porte du pod de test");
      else if (data.status === "error") toast.error("Resend refuse encore");
      else toast.success("Courriel de test parti");
    } catch (e) {
      setTest({ status: "error", error: e.response?.data?.detail || "Test impossible" });
      toast.error("Test impossible");
    } finally {
      setTesting(false);
    }
  };

  return (
    <section
      className="mb-8 rounded-xl border border-zinc-200 bg-white p-5"
      data-testid="resend-sentinel-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900 flex items-center gap-2">
            <PaperPlaneTilt size={20} weight="duotone" /> Sentinelle Resend — domaine d'envoi
          </h2>
          <p className="text-xs text-zinc-600 mt-1 max-w-2xl">
            Tant que le domaine n'est pas vérifié chez Resend, AUCUN courriel ne
            part : ni invitation, ni réinitialisation de mot de passe, ni
            alerte — et pas davantage en production. Publiez les enregistrements
            ci-dessous chez votre registraire, puis relancez le test.
          </p>
        </div>
        <Button
          onClick={runTest}
          disabled={testing}
          variant="outline"
          className="flex items-center gap-2"
          data-testid="resend-retest-btn"
        >
          {testing ? <SpinnerGap size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} />}
          {testing ? "Test en cours..." : "Relancer le test d'envoi"}
        </Button>
      </div>

      {test && (
        <p
          className={`text-sm rounded-lg px-3 py-2 mb-3 border flex items-start gap-2 ${
            test.status === "suppressed"
              ? "bg-amber-50 border-amber-200 text-amber-900"
              : test.status === "error"
                ? "bg-red-50 border-red-200 text-red-800"
                : "bg-teal-50 border-teal-200 text-teal-800"
          }`}
          data-testid="resend-test-result"
        >
          {test.status === "error" ? (
            <XCircle size={18} weight="fill" className="shrink-0 mt-0.5" />
          ) : (
            <CheckCircle size={18} weight="fill" className="shrink-0 mt-0.5" />
          )}
          <span>
            {test.status === "suppressed"
              ? "Envoi BLOQUÉ par la porte du pod de test (tâche 1227) : le chemin " +
                "est bon, mais rien ne part d'ici par défaut. Activez l'interrupteur " +
                "« Courriels depuis le pod de test » ci-dessus pour un vrai envoi."
              : test.status === "error"
                ? `Resend refuse : ${test.error || test.message || "raison non précisée"}`
                : `Courriel de test parti à votre adresse (identifiant ${test.id || test.email_id || "—"}).`}
          </span>
        </p>
      )}

      <EmailDomainGuide />
    </section>
  );
};

export default ResendSentinelCard;
